import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import {
  MessageBar,
  MessageBarType,
} from 'office-ui-fabric-react/lib/MessageBar';
import React, { Component, Fragment } from 'react';
import styled from 'styled-components';
import Loading from '../../components/LoadingOverlay';
import { localizable } from '../../components/Localization';
import Watermark from '../../components/Watermark';
import { getQueryStringValue } from '../../helpers/queryString';
import { websocketsUri } from '../../helpers/websockets';
import BiddingSection from './BiddingSection';
import Commands from './Commands';
import Guides from './Guides';
import OffersSection from './OffersSection';
import {
  BlockUser,
  GenericPrompt,
  OpenRoundConfirmation,
  UploadOfferConfirmation,
} from './Prompts';
import { RoundInfo, RoundSection } from './RoundSection';
import SaleEditSection from './SaleEditSection';
import SaleManageSection from './SaleManageSection';
import SaleNotificationsSection from './SaleNotificationsSection';
import SaleMessagingSection from './SaleMessagingSection';
import SellerInviteSection from './SellerInviteSection';
import WhitelistingSection from './WhitelistingSection';
import BuyerAgentInviteSection from './BuyerAgentInviteSection';

/*
const OpenRoundConfirmation = dynamicImport({
  component: '/containers/Sale/Prompts/OpenRoundConfirmation',
});
*/

const SaleMessageBar = styled(MessageBar)`
  position: fixed !important;
  top: 0;
  left: 10vw;
  width: 80vw !important;
  z-index: 1;

  background-color: #facfd3 !important;
`;

const TitleSection = styled.div`
  .label {
    align-self: center;

    font-size: 0.8rem !important;
    font-weight: bold !important;

    color: #fff;

    padding: 0.1em 0.8em 0.3em 0.8em;

    line-height: 1.2;

    &.with-icon {
      padding-left: 0.5em;
    }

    .ms-Icon {
      font-size: 0.8rem !important;
      margin-right: 0.5em;
    }
  }
`;

@inject('user', 'sale', 'app')
@localizable
@observer
class Sale extends Component {
  @observable currentPanel = null;

  componentDidMount() {
    const { sale, match } = this.props;
    if (!sale.connected) {
      const uri = websocketsUri(
        `/events/v1/sale?sid=${encodeURIComponent(match.params.sale)}`
      );
      sale.connect(uri);
    }

    sale.clearResponse();
  }

  componentWillUnmount() {
    const { sale } = this.props;
    sale.reset();
    sale.disconnect();
  }

  componentWillReact() {
    const { sale, history } = this.props;
    const preventRedirect = getQueryStringValue('r') === 'hold';
    if (sale.isDeleted && !preventRedirect) {
      history.push(`/dashboard`);
    }
  }

  handleBack = () => {
    const { history } = this.props;
    history.push(`/dashboard`);
  };

  handleDismissErrorBar = () => {
    const { sale } = this.props;
    sale.clearError();
  };

  render() {
    const { app, sale, history, user, t, match } = this.props;
    const { item, error } = sale;
    const { ui, round, offers, saleEdit, docs, whitelisting } = sale;

    if (sale.forbidden) {
      return this.renderErrorBar({ code: 'forbidden' });
    }
    if (sale.notFound) {
      return this.renderErrorBar({
        code: 'sale_not_found',
        text: 'This sale could not be found.',
      });
    }
    if (sale.unavailable) {
      return this.renderErrorBar({
        code: 'sale_connection_unavailable',
        text: 'Lost the connection to this sale. Please reload the page.',
      });
    }
    if (sale.isDeleted) {
      return this.renderErrorBar({ code: sale.status });
    }
    if (sale.saleId !== match.params.sale) {
      return <Loading label={t('Loading sale') + '...'} fullscreen={true} />;
    }

    const watermark =
      sale.isTest && !(app.config.watermark && app.config.watermark !== '');

    return (
      <Fragment>
        {watermark && (
          <Watermark data-test-id="test-watermark">
            {t('test_sale_watermark')}
          </Watermark>
        )}
        {this.renderErrorBar(error)}

        <TitleSection className="title">
          <a className="back" onClick={this.handleBack}>
            <i className="ms-Icon ms-Icon--Home" aria-hidden="true"></i>
            <span>{t('Back')}</span>
          </a>
          <span className="collapsible">
            {item.number}&nbsp;{item.title}
          </span>
          <RoundInfo round={round.definition} />
        </TitleSection>

        <div className="menu">
          <Commands store={sale} history={history} user={user} app={app} />
        </div>

        <div className="content">
          <Guides app={app} store={sale} t={t} theme={app.theme} />

          {ui.currentPanel === 'roundStart' && (
            <RoundSection store={round} ui={ui} />
          )}

          {ui.currentPanel === 'inviteSeller' && <SellerInviteSection />}

          {ui.currentPanel === 'inviteBuyerAgent' && (
            <BuyerAgentInviteSection />
          )}

          <SaleEditSection store={saleEdit} docs={docs} ui={ui} />

          <SaleManageSection sale={saleEdit} user={user} ui={ui} />

          <SaleNotificationsSection sale={saleEdit} user={user} ui={ui} />

          <WhitelistingSection
            whitelisting={whitelisting}
            sale={saleEdit}
            user={user}
            ui={ui}
          />

          <SaleMessagingSection sale={saleEdit} user={user} ui={ui} />

          <OffersSection store={offers} ui={ui} />

          {ui.currentPanel === 'biddingHistory' && (
            <BiddingSection
              store={sale.bidding}
              closePanel={() => ui.closePanel()}
            />
          )}

          {(sale.ui.prompt.openRound && (
            <OpenRoundConfirmation
              sale={sale}
              app={app}
              user={user}
              saleEdit={saleEdit}
              t={t}
              locale={this.props.intl.locale}
            />
          )) ||
            (sale.ui.prompt.block && <BlockUser sale={sale} t={t} />) ||
            (sale.ui.prompt.upload && (
              <UploadOfferConfirmation sale={sale} saleEdit={saleEdit} t={t} />
            )) ||
            (sale.ui.prompt.opened && <GenericPrompt sale={sale} />)}

          <Loading
            label={t('Processing') + '...'}
            fullscreen={true}
            isVisible={ui.loading.hasPenddingActions}
            opacity={0.9}
          />
        </div>
      </Fragment>
    );
  }

  renderErrorBar = (err) => {
    const { t } = this.props;
    return err ? (
      <SaleMessageBar
        messageBarType={MessageBarType.error}
        isMultiline={true}
        onDismiss={this.handleDismissErrorBar}
      >
        {err.code && <div>{t(err.code, null, err.text)}</div>}
        {!err.code && err.message && <div>{err.message}</div>}
      </SaleMessageBar>
    ) : (
      <Fragment></Fragment>
    );
  };
}

export default Sale;



// WEBPACK FOOTER //
// ./src/app/containers/Sale/index.jsx